import React, { useState, useEffect } from "react";
import {
  IconFolderPlus,
  IconMedicalCross,
  IconSearch,
  IconClipboardList,
} from "@tabler/icons-react";
import styles from "../dashboard.module.css";
import { useNavigate } from "react-router-dom";
import { MDocumentUploadOptions } from "@/components/ui/molecules/MDocumentUploadOptions";
import {
  setNavigationSource,
  clearNavigationSource,
} from "@/utils/navigationSource";
import { getMemberInfo } from "@/hooks/useMembers";

interface DashboardQuickActionsProps {
  onAddHealthMemory?: () => void;
  onAddDocument?: () => void;
  onAddFamilyNote?: () => void;
  healthSummaryReady?: boolean;
  emergencyCardReady?: boolean;
}

const DashboardQuickActions: React.FC<DashboardQuickActionsProps> = ({
  healthSummaryReady = false,
  emergencyCardReady = false,
}) => {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { isExperienced } = getMemberInfo(true);

  // Clear navigationSource when dashboard loads
  useEffect(() => {
    clearNavigationSource();
  }, []);

  // Dynamic actions based on emergencyCardReady and healthSummaryReady
  // Priority: Always Emergency Card > Health Summary/Upload Document > Search Document
  const getHealthAction = () => {
    if (emergencyCardReady) {
      return {
        icon: <IconMedicalCross size={32} color="#8764BB" stroke={2} />,
        label: "Your Emergency \n card",
        action: () =>
          navigate("/dashboard/emergency-card", {
            state: { backUrl: "/dashboard" },
          }),
      };
    } else {
      return {
        icon: <IconMedicalCross size={32} color="#8764BB" stroke={2} />,
        label: "Create your \n emergency card",
        action: () =>
          navigate("/profile/emergency-card/activate", {
            state: { backUrl: "/dashboard" },
          }),
      };
    }
  };

  const getSecondAction = () => {
    if (healthSummaryReady) {
      return {
        icon: <IconClipboardList size={32} color="#8764BB" stroke={2} />,
        label: "Your Health \n Summary",
        action: () => {
          setNavigationSource("overview", "/dashboard");
          navigate("/health-summary", { state: { backUrl: "/dashboard" } });
        },
      };
    } else {
      return {
        icon: <IconFolderPlus size={32} color="#8764BB" stroke={2} />,
        label: "Upload\ndocument",
        action: () => setShowUploadModal(true),
      };
    }
  };

  const healthAction = getHealthAction();
  const secondAction = getSecondAction();

  const actions = [
    {
      icon: healthAction.icon,
      label: healthAction.label,
      key: "health",
    },
    {
      icon: secondAction.icon,
      label: secondAction.label,
      key: "second",
    },
    {
      icon: <IconSearch size={32} color="#8764BB" stroke={2} />,
      label: "Search\ndocument",
      key: "search",
    },
  ];

  const handleHealthAction = () => {
    healthAction.action();
  };

  const handleSecondAction = () => {
    secondAction.action();
  };

  const handleSearchDocument = () => {
    navigate("/documents", { state: { backUrl: "/dashboard" } });
  };

  return (
    <>
      <div
        className={`${styles.quickActionsCard} ${
          isExperienced ? "" : styles.quickActionsCardNewUser
        }`}
      >
        {actions.map((action) => (
          <button
            key={action.key}
            className={styles.quickActionBtn}
            onClick={
              action.key === "health"
                ? handleHealthAction
                : action.key === "second"
                ? handleSecondAction
                : handleSearchDocument
            }
          >
            <span className={styles.quickActionIconWrapper}>{action.icon}</span>
            <span className={styles.quickActionLabel}>
              {action.label.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i === 0 && <br />}
                </React.Fragment>
              ))}
            </span>
          </button>
        ))}
      </div>
      <MDocumentUploadOptions
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />
    </>
  );
};

export default DashboardQuickActions;
